import {
  SupportCenterContentViewModel,
  SupportCenterSortCombo,
} from '@app/support-center/model/support-center-model';
import { PlatformEnum } from '@panamax/app-state';
import { ImageVariantEnum } from '@product-detail/models/image-variant.enum';
import { getFirstImageURL } from '@product-detail/utils/product-images.util';
import { SortableSectionHeader } from '@shared/components/sortable-section/models/sortable-section.model';
import { Product } from '@shared/models/product.model';
import { ServiceRequestType, SousStatus } from '@usf/list-types';
import {
  SupportCenterConstants,
  SupportCenterSortOptions,
  mapSousStatusToNumber,
} from '@usf/ngrx-list';
import {
  RequestLookup,
  SupportCenterHeaders,
} from '@usf/ngrx-list/lib/models/support-center/support-center.model';
import { ServiceRequestSupportCenterContentState } from '@usf/ngrx-list';

export const getSupportCenterItemSize = (platform: PlatformEnum): number => {
  return platform === PlatformEnum.mobile ? 145 : 70;
};

export const transformSupportCenterItemToViewModel = (
  supportItem: ServiceRequestSupportCenterContentState,
  product: Product,
  creditLookup: RequestLookup,
  pickupLookup: RequestLookup,
  recoveryLookup: RequestLookup,
  supportLookup: RequestLookup,
): SupportCenterContentViewModel => {
  let issueDetailValues = {
    issue: 'N/A',
    issueDetail: 'N/A',
  };

  let supportCenterUserInfo = {
    firstName: '',
    lastName: '',
    userName: '',
  };
  if (!!supportItem.submittedByUser?.firstName) {
    supportCenterUserInfo.firstName = supportItem.submittedByUser.firstName;
  }
  if (!!supportItem.submittedByUser?.lastName) {
    supportCenterUserInfo.lastName = supportItem.submittedByUser.lastName;
  }
  if (!!supportItem.submittedByUser?.userName) {
    supportCenterUserInfo.userName = supportItem.submittedByUser.userName;
  }

  switch (supportItem.serviceRequestType) {
    case ServiceRequestType.PICKUP_REQUEST:
      issueDetailValues = pickupLookup[supportItem.issue];
      break;
    case ServiceRequestType.RECOVERY_REQUEST:
      issueDetailValues = recoveryLookup[supportItem.issue];
      break;
    case ServiceRequestType.REQUEST_CREDIT:
      issueDetailValues =
        creditLookup[supportItem.issue + '-' + supportItem.issueDetail];
      break;
    case ServiceRequestType.SUPPORT_REQUEST:
      issueDetailValues = supportLookup[supportItem.issue];
      break;
  }
  const isNewSupportRequest =
    supportItem.serviceRequestType === ServiceRequestType.SUPPORT_REQUEST &&
    supportItem.status === SousStatus.NEW;

  return {
    ...supportItem,
    productNumber: Number(supportItem.productNumber).valueOf(),
    productName: product?.summary?.productDescLong,
    productImageUrl: getFirstImageURL(
      product?.summary,
      ImageVariantEnum.Thumbnail,
    ),
    statusText: isNewSupportRequest ? 'N/A' : getStatusText(supportItem.status),
    issue: issueDetailValues?.issue ?? 'N/A',
    issueDetail: issueDetailValues?.issueDetail ?? 'N/A',
    submittedByUser: supportCenterUserInfo,
    requestedDeliveryDate: supportItem.requestDeliveryDate,
  };
};

export const getStatusText = (status: SousStatus) => {
  switch (status) {
    case SousStatus.NEW:
      return 'i18n.supportCenter.status.new';
    case SousStatus.FAILED:
      return 'i18n.supportCenter.status.fail';
    case SousStatus.APPROVED:
      return 'i18n.supportCenter.status.approve';
    case SousStatus.CREATED:
      return 'i18n.supportCenter.status.waitingForApproval';
    case SousStatus.PENDING_CDMR_DECISION:
      return 'i18n.supportCenter.status.waitingForApproval';
    case SousStatus.IN_PROGRESS:
      return 'i18n.supportCenter.status.inProgress';
    case SousStatus.CLOSED:
      return 'i18n.supportCenter.status.close';
    default:
      return '';
  }
};

export const isStatusHigherOrder = (
  status1: SousStatus,
  status2: SousStatus,
) => {
  return mapSousStatusToNumber(status1) - mapSousStatusToNumber(status2);
};

export const reverseChronologicalCheck = (date1: Date, date2: Date) => {
  return date2.getTime() - date1.getTime();
};

export const transformSupportCenterSort = (
  headers: SupportCenterHeaders,
  platform: PlatformEnum,
) => {
  const sortableHeaders: SortableSectionHeader[] = [];
  for (const [key, header] of Object.entries(headers)) {
    switch (key) {
      case SupportCenterSortOptions.product:
        sortableHeaders.push({
          headerName: key,
          sortDirection: header.sortDirection,
          displayText: header.displayText,
          size: platform === PlatformEnum.desktop ? 1.65 : 2,
          offset: platform === PlatformEnum.desktop ? 0.225 : 0.3,
        });
        break;
      case SupportCenterSortOptions.request:
        sortableHeaders.push({
          headerName: key,
          sortDirection: header.sortDirection,
          displayText: header.displayText,
          size: platform === PlatformEnum.desktop ? 1.9 : 3.5,
          offset: platform === PlatformEnum.desktop ? 0.18 : 0.4,
        });
        break;
      case SupportCenterSortOptions.status:
        sortableHeaders.push({
          headerName: key,
          sortDirection: header.sortDirection,
          displayText: header.displayText,
          size: platform === PlatformEnum.desktop ? 1.266 : 1.5,
          offset: platform === PlatformEnum.desktop ? 0.18 : 0.5,
        });
        break;
      case SupportCenterSortOptions.invoice:
        sortableHeaders.push({
          headerName: key,
          sortDirection: header.sortDirection,
          displayText: header.displayText,
          size: platform === PlatformEnum.desktop ? 1 : 2,
          offset: platform === PlatformEnum.desktop ? 0.18 : 0.5,
        });
        break;
      case SupportCenterSortOptions.order:
        if (platform === PlatformEnum.desktop) {
          sortableHeaders.push({
            headerName: key,
            sortDirection: header.sortDirection,
            displayText: header.displayText,
            size: 0.8,
            offset: 0.18,
          });
        }
        break;
      case SupportCenterSortOptions.date:
        if (platform === PlatformEnum.desktop) {
          sortableHeaders.push({
            headerName: key,
            sortDirection: header.sortDirection,
            displayText: header.displayText,
            size: 0.75,
            offset: 0.13,
          });
        }
        break;
      case SupportCenterSortOptions.username:
        if (platform === PlatformEnum.desktop) {
          sortableHeaders.push({
            headerName: key,
            sortDirection: header.sortDirection,
            displayText: header.displayText,
            offset: 0.18,
          });
        }
        break;
    }
  }
  return sortableHeaders;
};

export const sortSupportCenterItems = (
  selectedHeader: string,
  sortDirection: string,
  supportItems: SupportCenterContentViewModel[],
) => {
  const sortedSupportItems = supportItems.sort((a, b) => {
    let valueA: SupportCenterSortCombo;
    let valueB: SupportCenterSortCombo;
    switch (selectedHeader) {
      case SupportCenterSortOptions.product:
        valueA = {
          value: a.productNumber,
          status: a.status,
        };
        valueB = {
          value: b.productNumber,
          status: b.status,
        };
        break;
      case SupportCenterSortOptions.request:
        valueA = {
          value: a.caseNumber,
          status: a.status,
        };
        valueB = {
          value: b.caseNumber,
          status: b.status,
        };
        break;
      case SupportCenterSortOptions.status:
        valueA = {
          value: a.submissionDate,
          status: a.status,
        };
        valueB = {
          value: b.submissionDate,
          status: b.status,
        };
        break;
      case SupportCenterSortOptions.invoice:
        valueA = {
          value: a.invoiceNumber,
          status: a.status,
        };
        valueB = {
          value: b.invoiceNumber,
          status: b.status,
        };
        break;
      case SupportCenterSortOptions.order:
        valueA = {
          value: a.orderNumber,
          status: a.status,
        };
        valueB = {
          value: b.orderNumber,
          status: b.status,
        };
        break;
      case SupportCenterSortOptions.date:
        valueA = {
          value: new Date(new Date(a.submissionDate).toDateString()).getTime(),
          status: a.status,
        };
        valueB = {
          value: new Date(new Date(b.submissionDate).toDateString()).getTime(),
          status: b.status,
        };
        break;
      case SupportCenterSortOptions.username:
        if (!!a.submittedByUser.firstName && !!a.submittedByUser.userName) {
          const fullName =
            a.submittedByUser.firstName +
            ' ' +
            a.submittedByUser.lastName +
            '( ' +
            a.submittedByUser.userName +
            ')';
          valueA = {
            value: fullName,
            status: a.status,
          };
        } else if (!!a.submittedByUser.firstName) {
          const fullName =
            a.submittedByUser.firstName + ' ' + a.submittedByUser.lastName;
          valueA = {
            value: fullName,
            status: a.status,
          };
        } else {
          valueA = {
            value: 'System',
            status: a.status,
          };
        }
        if (!!b.submittedByUser.firstName && !!b.submittedByUser.userName) {
          const fullName =
            b.submittedByUser.firstName +
            ' ' +
            b.submittedByUser.lastName +
            '( ' +
            b.submittedByUser.userName +
            ')';
          valueB = {
            value: fullName,
            status: b.status,
          };
        } else if (!!b.submittedByUser.firstName) {
          const fullName =
            b.submittedByUser.firstName + ' ' + b.submittedByUser.lastName;
          valueB = {
            value: fullName,
            status: b.status,
          };
        } else {
          valueB = {
            value: 'System',
            status: b.status,
          };
        }
        break;
    }
    return sortSupportRequests(valueA, valueB, sortDirection, selectedHeader);
  });
  return sortedSupportItems;
};

export const sortSupportRequests = (
  a: any,
  b: any,
  sortDirection: string,
  header: string,
) => {
  const collator = new Intl.Collator('en', {
    numeric: header === SupportCenterSortOptions.username ? false : true,
    caseFirst: 'upper',
    sensitivity: 'base',
  });
  if (header === SupportCenterSortOptions.status) {
    const statusCheck =
      sortDirection === SupportCenterConstants.asc
        ? isStatusHigherOrder(a.status, b.status)
        : isStatusHigherOrder(b.status, a.status);
    if (statusCheck === 0) {
      return reverseChronologicalCheck(new Date(a.value), new Date(b.value));
    } else {
      return statusCheck;
    }
  } else {
    if (!a && !!b) {
      return sortDirection === SupportCenterConstants.asc ? 1 : -1;
    } else if (!!a && !b) {
      return sortDirection === SupportCenterConstants.asc ? -1 : 1;
    } else {
      const sortValue =
        sortDirection === SupportCenterConstants.asc
          ? collator.compare(a.value, b.value)
          : collator.compare(b.value, a.value);
      if (sortValue === 0) {
        return sortDirection === SupportCenterConstants.asc
          ? isStatusHigherOrder(a.status, b.status)
          : isStatusHigherOrder(b.status, a.status);
      } else {
        return sortValue;
      }
    }
  }
};
